/**
 * Client Error Tracker — Sentry-light для проекта.
 *
 * Ловит:
 *   1. window.onerror             — глобальные синхронные JS-ошибки
 *   2. unhandledrejection         — необработанные promise rejection
 *   3. window.addEventListener('error', ...) — ошибки загрузки ресурсов (img, script, css)
 *   4. fetch failures              — ошибки HTTP (через monkey-patch)
 *   5. console.error               — программные ошибки кода (опционально, флаг)
 *
 * Каждая ошибка отправляется через captureEvent('client_error') с детальным
 * контекстом: message, stack, url, line:col, viewport, ua, last_user_action.
 *
 * Защита от спама:
 *   - dedup по сигнатуре (message+stack) в течение 60 секунд
 *   - максимум 50 ошибок за сессию
 *   - игнорирование известных safe-to-ignore паттернов
 */

import { captureEvent } from './index';

// ─── Конфиг ──────────────────────────────────────────────────────────────────

const MAX_ERRORS_PER_SESSION = 50;
const DEDUP_WINDOW_MS = 60 * 1000;
const MAX_BREADCRUMBS = 30;

/**
 * Паттерны ошибок, которые шумят и не дают полезной информации.
 * Обычно — расширения браузера, реклама, "ResizeObserver loop", и пр.
 */
const IGNORE_PATTERNS: RegExp[] = [
  /ResizeObserver loop/i,
  /Non-Error promise rejection captured/i,
  /Script error\.?$/i,                       // cross-origin без CORS
  /Loading chunk \d+ failed/i,               // sport.js, можно tracking но мы не будем
  /ChunkLoadError/i,
  /^(?:NetworkError|Failed to fetch)$/i,    // если apiClient успешно retry'нул
  /AbortError/i,                             // отмена fetch — не баг
  /The user aborted a request/i,
  /chrome-extension:/i,
  /moz-extension:/i,
  /safari-extension:/i,
  /at <unknown>/i,
];

// Какие URL не считать (внешние трекеры, реклама, аналитика — не наш код, не наши ошибки)
const IGNORE_URL_PATTERNS: RegExp[] = [
  /mc\.yandex\.ru/i,
  /top-fwz1\.mail\.ru/i,
  /vk\.com\/rtrg/i,
  /googletagmanager\.com/i,
  /google-analytics\.com/i,
  /facebook\.com\/tr/i,           // Meta Pixel
  /connect\.facebook\.net/i,
  /static\.criteo\.net/i,
  /gum\.criteo\.com/i,
  // Next.js RSC prefetch — браузер предзагружает следующую страницу при наведении
  // курсора на <Link>. Если сеть подвисла — prefetch падает, но реальная навигация
  // НЕ страдает (Next.js просто загрузит при клике). Не считаем за ошибку.
  /[?&]_rsc=/i,
];

// ─── State ───────────────────────────────────────────────────────────────────

let initialized = false;
let errorsSentInSession = 0;
const recentErrors = new Map<string, number>(); // signature -> timestamp

// Breadcrumbs — последние действия пользователя (для context при ошибке)
interface Breadcrumb {
  ts: number;
  type: 'click' | 'navigation' | 'fetch' | 'console' | 'input' | 'custom';
  message: string;
}
const breadcrumbs: Breadcrumb[] = [];

function addBreadcrumb(crumb: Omit<Breadcrumb, 'ts'>): void {
  breadcrumbs.push({ ts: Date.now(), ...crumb });
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
}

function snapshotBreadcrumbs(): Array<{ rel_ms: number; type: string; message: string }> {
  const now = Date.now();
  return breadcrumbs.slice(-15).map((c) => ({
    rel_ms: c.ts - now, // отрицательное: -1500 = 1.5 сек назад
    type: c.type,
    message: c.message,
  }));
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function shouldIgnore(message: string, source?: string): boolean {
  if (!message) return true;
  for (const re of IGNORE_PATTERNS) if (re.test(message)) return true;
  if (source) {
    for (const re of IGNORE_URL_PATTERNS) if (re.test(source)) return true;
  }
  return false;
}

function makeSignature(message: string, stack?: string): string {
  // Сигнатура = первые 100 символов message + первый кадр стека
  const firstFrame = (stack || '').split('\n').slice(0, 2).join('|');
  return (message.slice(0, 100) + '|' + firstFrame).slice(0, 250);
}

function isDuplicate(signature: string): boolean {
  const now = Date.now();
  // Чистим старые записи
  for (const [sig, ts] of recentErrors) {
    if (now - ts > DEDUP_WINDOW_MS) recentErrors.delete(sig);
  }
  if (recentErrors.has(signature)) return true;
  recentErrors.set(signature, now);
  return false;
}

function truncateStack(stack: string | undefined, maxLen = 2000): string | null {
  if (!stack) return null;
  return stack.length > maxLen ? stack.slice(0, maxLen) + '\n…[truncated]' : stack;
}

/**
 * Контекст состояния сети — критичен для диагностики "почему сайт не работает"
 */
interface NetworkContext {
  online: boolean | null;
  connection_type: string | null;       // 'wifi' | '4g' | '3g' | '2g' | 'slow-2g' | etc
  downlink: number | null;              // Mbps
  rtt: number | null;                   // round-trip time (ms)
  save_data: boolean | null;            // юзер включил «экономию трафика»
  time_since_pageload_ms: number | null;
}

function captureNetworkContext(): NetworkContext {
  if (typeof window === 'undefined') {
    return { online: null, connection_type: null, downlink: null, rtt: null, save_data: null, time_since_pageload_ms: null };
  }
  const nav = navigator as Navigator & {
    connection?: { effectiveType?: string; downlink?: number; rtt?: number; saveData?: boolean };
  };
  const conn = nav.connection;
  return {
    online: typeof nav.onLine === 'boolean' ? nav.onLine : null,
    connection_type: conn?.effectiveType || null,
    downlink: conn?.downlink || null,
    rtt: conn?.rtt || null,
    save_data: conn?.saveData ?? null,
    time_since_pageload_ms: typeof performance !== 'undefined' ? Math.round(performance.now()) : null,
  };
}

/**
 * Детектируем browser extensions и third-party скрипты, которые мутируют DOM
 * до гидратации React. Это ТОП-1 причина React #418 в production.
 *
 * Возвращает массив строк-меток обнаруженных модификаций.
 */
function detectDomMutators(): string[] {
  if (typeof document === 'undefined') return [];
  const found: string[] = [];
  const html = document.documentElement;
  const body = document.body;

  // ─── Browser extensions ───
  // Grammarly — вставляет data-gr-* атрибуты в input и body
  if (body?.hasAttribute('data-gr-c-s-loaded') || document.querySelector('grammarly-extension')) {
    found.push('extension:grammarly');
  }
  if (html?.hasAttribute('cz-shortcut-listen')) {
    found.push('extension:colorzilla');
  }
  // Dark Reader — добавляет class на html и meta theme-color
  if (html?.classList.contains('darkreader') || document.querySelector('meta[name="darkreader"]')) {
    found.push('extension:darkreader');
  }
  // LastPass — вставляет input data-lastpass-icon-root
  if (document.querySelector('[data-lastpass-icon-root]')) {
    found.push('extension:lastpass');
  }
  // 1Password
  if (document.querySelector('[data-com-onepassword-filled]')) {
    found.push('extension:1password');
  }
  // AdBlock — обычно через DOM injection с классом ".adblock-*"
  if (html?.hasAttribute('data-adblockkey') || document.querySelector('.adblock-detector')) {
    found.push('extension:adblock');
  }
  // Google Translate — оборачивает текст в <font>
  if (html?.classList.contains('translated-ltr') || html?.classList.contains('translated-rtl')) {
    found.push('extension:google-translate');
  }
  // Метки от Яндекс.Браузера расширений ("Защитник", "Турбо")
  if (document.querySelector('ya-tr-span') || document.querySelector('[class*="ya-search-"]')) {
    found.push('extension:yandex-browser');
  }
  // Honey, Rakuten — купонные расширения
  if (document.querySelector('[id^="honey-"]') || document.querySelector('[class^="honey-"]')) {
    found.push('extension:honey');
  }

  // ─── Third-party scripts мутирующие DOM ───
  // Yandex Metrika - injects iframes
  if ((window as unknown as { ym?: unknown }).ym || document.querySelector('iframe[src*="mc.yandex.ru"]')) {
    found.push('script:yandex-metrika');
  }
  // VK Pixel
  if ((window as unknown as { VK?: unknown }).VK || document.querySelector('script[src*="vk.com/js/api/openapi"]')) {
    found.push('script:vk-pixel');
  }
  // Top.Mail.Ru
  if ((window as unknown as { _tmr?: unknown })._tmr) {
    found.push('script:tmr');
  }
  // Meta Pixel
  if ((window as unknown as { fbq?: unknown }).fbq) {
    found.push('script:meta-pixel');
  }
  // Jivosite / online chat виджеты
  if (document.querySelector('jdiv') || (window as unknown as { jivo_api?: unknown }).jivo_api) {
    found.push('script:jivosite');
  }
  if (document.querySelector('.b24-widget-button-wrapper')) {
    found.push('script:bitrix24-chat');
  }

  return found;
}

/**
 * Снимок ключевых атрибутов <html> и <body> в момент ошибки.
 * Это даёт прямое доказательство кто менял DOM (theme provider, расширения).
 */
function snapshotHtmlAttributes(): { html_class: string | null; html_attrs: string; body_class: string | null; meta_theme: string | null } {
  if (typeof document === 'undefined') {
    return { html_class: null, html_attrs: '', body_class: null, meta_theme: null };
  }
  const html = document.documentElement;
  const body = document.body;
  const attrs = html ? Array.from(html.attributes).map((a) => `${a.name}="${a.value.slice(0, 40)}"`).join(' ') : '';
  const meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
  return {
    html_class: html?.className?.slice(0, 200) || null,
    html_attrs: attrs.slice(0, 500),
    body_class: body?.className?.slice(0, 200) || null,
    meta_theme: meta?.content || null,
  };
}

/**
 * Маркер hydration-ошибки — нужно собрать максимум контекста.
 */
function isHydrationError(message: string): boolean {
  return /Minified React error #(418|419|420|421|422|423|425)|Hydration failed|Text content does not match|did not match/i.test(
    message
  );
}

function sendError(payload: {
  errorType: 'js' | 'promise' | 'resource' | 'fetch' | 'console';
  message: string;
  stack?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  status?: number;
  url?: string;
  method?: string;
}): void {
  if (errorsSentInSession >= MAX_ERRORS_PER_SESSION) return;
  if (shouldIgnore(payload.message, payload.source || payload.url)) return;

  const signature = makeSignature(payload.message, payload.stack);
  if (isDuplicate(signature)) return;

  errorsSentInSession++;

  const network = captureNetworkContext();

  // Для hydration-ошибок (#418/420/422/etc.) собираем ДОПОЛНИТЕЛЬНЫЙ контекст —
  // кто мутировал DOM до гидратации React (это ТОП-1 причина в production).
  const isHydration = isHydrationError(payload.message);
  const domMutators = isHydration ? detectDomMutators() : null;
  const htmlSnapshot = isHydration ? snapshotHtmlAttributes() : null;

  captureEvent('client_error', {
    error_type: payload.errorType,
    message: payload.message.slice(0, 500),
    stack: truncateStack(payload.stack),
    source: payload.source?.slice(0, 500) || null,
    lineno: payload.lineno || null,
    colno: payload.colno || null,
    status: payload.status || null,
    request_url: payload.url?.slice(0, 500) || null,
    method: payload.method || null,
    page_url: typeof window !== 'undefined' ? window.location.href : null,
    page_path: typeof window !== 'undefined' ? window.location.pathname : null,
    viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : null,
    referrer: typeof document !== 'undefined' ? document.referrer : null,
    // Контекст сети — ключевое для диагностики "сайт не загружается у клиента":
    // online=false → клиент офлайн в момент ошибки
    // connection_type=2g/slow-2g → плохой мобильный интернет
    // save_data=true → клиент включил экономию трафика → API могут быть урезаны
    // rtt > 1000 → высокая задержка (плохая связь / далёкий регион)
    online: network.online,
    connection_type: network.connection_type,
    downlink_mbps: network.downlink,
    rtt_ms: network.rtt,
    save_data: network.save_data,
    time_since_pageload_ms: network.time_since_pageload_ms,
    // Последние 15 действий пользователя перед ошибкой
    breadcrumbs: snapshotBreadcrumbs(),
    // ─── HYDRATION-СПЕЦИФИЧНЫЕ ПОЛЯ ───
    // Заполняются только для React #418/420/422 и аналогов.
    // Покажут, кто мутировал DOM ДО гидратации (виновник реальный).
    is_hydration_error: isHydration,
    dom_mutators: domMutators,            // массив меток: ['extension:grammarly', 'script:yandex-metrika']
    html_class: htmlSnapshot?.html_class || null,
    html_attrs: htmlSnapshot?.html_attrs || null,
    body_class: htmlSnapshot?.body_class || null,
    meta_theme: htmlSnapshot?.meta_theme || null,
  });
}

// ─── Listeners ───────────────────────────────────────────────────────────────

function onError(event: ErrorEvent): void {
  // Если e.target — это HTMLElement (img/script/link что-то не загрузилось)
  const target = event.target as HTMLElement | null;
  if (target && target.tagName && target !== (window as unknown as HTMLElement)) {
    const src = (target as HTMLImageElement).src
              || (target as HTMLScriptElement).src
              || (target as HTMLLinkElement).href
              || '';
    if (src && !shouldIgnore('', src)) {
      sendError({
        errorType: 'resource',
        message: `Failed to load <${target.tagName.toLowerCase()}>: ${src}`,
        source: src,
      });
    }
    return;
  }

  // Обычная JS-ошибка
  sendError({
    errorType: 'js',
    message: event.message || event.error?.message || 'Unknown error',
    stack: event.error?.stack,
    source: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  const reason = event.reason;

  // Игнорируем пустые rejection — это шум от сторонних SDK (YM)
  // и AbortError при навигации (pending fetch отменяется window.location.href).
  if (reason === null || reason === undefined) return;

  // AbortError кидается браузером при отмене fetch (например, при навигации
  // на другую страницу). Это нормально — не наша ошибка.
  if (reason && typeof reason === 'object') {
    const name = (reason as { name?: unknown }).name;
    if (name === 'AbortError' || name === 'CanceledError') return;
  }

  const message = typeof reason === 'string'
    ? reason
    : (reason?.message || reason?.toString?.() || 'Unhandled promise rejection');
  const stack = reason?.stack;

  // Финальный фильтр — если получился фоллбэк-текст "Unhandled promise rejection"
  // без какой-либо полезной информации — не шлём. Это всегда шум.
  if (message === 'Unhandled promise rejection' && !stack) return;

  sendError({
    errorType: 'promise',
    message: String(message),
    stack: stack ? String(stack) : undefined,
  });
}

// ─── Fetch monkey-patch (ловит API-ошибки и network failures) ────────────────

function patchFetch(): void {
  if (typeof window === 'undefined' || !window.fetch) return;
  const origFetch = window.fetch;

  window.fetch = async function trackedFetch(input: RequestInfo | URL, init?: RequestInit) {
    const method = init?.method?.toUpperCase() || 'GET';
    const url = typeof input === 'string'
      ? input
      : input instanceof URL ? input.href
      : input instanceof Request ? input.url
      : String(input);

    // Не трекаем сам tracking-endpoint (избегаем бесконечной петли)
    if (url.includes('/api/log/')) return origFetch(input, init);

    try {
      const response = await origFetch(input, init);
      // 4xx/5xx — считаем ошибкой API (но не 401/403 — это auth flow, нормально)
      if (response.status >= 400 && response.status !== 401 && response.status !== 403) {
        // Breadcrumb с URL — чтобы при последующей ошибке мы видели контекст
        addBreadcrumb({
          type: 'fetch',
          message: `✗ HTTP ${response.status} ${method} ${shortUrl(url)}`,
        });
        sendError({
          errorType: 'fetch',
          message: `HTTP ${response.status} ${response.statusText || ''} ${method} ${url}`,
          status: response.status,
          url,
          method,
        });
      }
      return response;
    } catch (err) {
      const error = err as Error;
      // Breadcrumb с URL — критично для диагностики "Network Error" в трекере
      addBreadcrumb({
        type: 'fetch',
        message: `✗ NET ${method} ${shortUrl(url)} (${error.message})`,
      });
      // Сетевая ошибка (offline, DNS, CORS)
      sendError({
        errorType: 'fetch',
        message: `Network error: ${error.message} ${method} ${url}`,
        stack: error.stack,
        url,
        method,
      });
      throw err; // важно — пробрасываем дальше
    }
  };
}

/**
 * Укорочение URL для breadcrumb — оставляем только path + query (без домена).
 * Полные URL занимают много места и плохо читаются в логе.
 */
function shortUrl(url: string): string {
  if (!url) return '';
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    return (u.pathname + u.search).slice(0, 120);
  } catch {
    return url.slice(0, 120);
  }
}

// ─── XHR monkey-patch (для legacy кода и third-party libs) ───────────────────

interface TrackedXHR extends XMLHttpRequest {
  __azMethod?: string;
  __azUrl?: string;
}

function patchXHR(): void {
  if (typeof window === 'undefined' || !window.XMLHttpRequest) return;
  const proto = XMLHttpRequest.prototype;
  const origOpen = proto.open;
  const origSend = proto.send;

  proto.open = function trackedOpen(this: TrackedXHR, method: string, url: string | URL, ...rest: unknown[]) {
    this.__azMethod = method.toUpperCase();
    this.__azUrl = typeof url === 'string' ? url : url.href;
    // @ts-expect-error — пробрасываем оставшиеся args без типизации
    return origOpen.apply(this, [method, url, ...rest]);
  };

  proto.send = function trackedSend(this: TrackedXHR, body?: Document | XMLHttpRequestBodyInit | null) {
    const url = this.__azUrl || '';
    const method = this.__azMethod || 'GET';

    if (!url.includes('/api/log/')) {
      this.addEventListener('load', () => {
        if (this.status >= 400 && this.status !== 401 && this.status !== 403) {
          addBreadcrumb({
            type: 'fetch',
            message: `✗ HTTP ${this.status} ${method} ${shortUrl(url)}`,
          });
          sendError({
            errorType: 'fetch',
            message: `XHR ${this.status} ${this.statusText || ''} ${method} ${url}`,
            status: this.status,
            url,
            method,
          });
        }
      });
      this.addEventListener('error', () => {
        addBreadcrumb({
          type: 'fetch',
          message: `✗ NET ${method} ${shortUrl(url)} (XHR error)`,
        });
        sendError({
          errorType: 'fetch',
          message: `XHR network error ${method} ${url}`,
          url,
          method,
        });
      });
      this.addEventListener('timeout', () => {
        addBreadcrumb({
          type: 'fetch',
          message: `✗ TIMEOUT ${method} ${shortUrl(url)}`,
        });
        sendError({
          errorType: 'fetch',
          message: `XHR timeout ${method} ${url}`,
          url,
          method,
        });
      });
    }

    return origSend.call(this, body as XMLHttpRequestBodyInit | null);
  };
}

// ─── Console.error patch (для библиотечных ошибок) ───────────────────────────

const CONSOLE_IGNORE_PATTERNS = [
  /Each child in a list should have/i,    // React key warnings — спам
  /validateDOMNesting/i,                  // dev-only warning
  /^\[HMR\]/,                              // hot module reload (только в dev)
  /^\[FastRefresh\]/,
  // ❗ Hydration warnings НЕ фильтруем — там React пишет имя компонента-виновника:
  //   "Hydration failed because the server rendered HTML didn't match the client.
  //    Server: ... Client: ..."
  // Это критически важно для диагностики #418 — оттуда мы узнаём ЧТО именно
  // не совпало (text content, attribute, missing child, etc.)
];

/**
 * Распознаёт строку как React component stack по характерному паттерну
 * нескольких "    at ComponentName" / "    at div" подряд.
 * React пишет componentStack как ОТДЕЛЬНЫЙ аргумент в console.error.
 */
function looksLikeComponentStack(s: string): boolean {
  if (typeof s !== 'string' || s.length < 20) return false;
  // Минимум 2 строки вида "    at SomeName" или "\n    at SomeName"
  const matches = s.match(/\n?\s+at\s+[A-Za-z_$][\w$]*/g);
  return !!matches && matches.length >= 2;
}

function patchConsoleError(): void {
  if (typeof console === 'undefined' || !console.error) return;
  const origError = console.error;

  console.error = function trackedConsoleError(...args: unknown[]) {
    try {
      // 1. Извлекаем компоненты сообщения. От Error берём И message И stack.
      let componentStack: string | null = null;
      let firstErrorStack: string | undefined;
      const messageParts: string[] = [];

      for (const a of args) {
        if (a instanceof Error) {
          if (!firstErrorStack) firstErrorStack = a.stack;
          messageParts.push(`${a.name}: ${a.message}`);
        } else if (typeof a === 'string') {
          // React component stack приходит отдельным строковым аргументом
          if (!componentStack && looksLikeComponentStack(a)) {
            componentStack = a;
          } else {
            messageParts.push(a);
          }
        } else if (a && typeof a === 'object') {
          // React 19 иногда передаёт errorInfo = { componentStack: "..." }
          const obj = a as { componentStack?: unknown; digest?: unknown };
          if (!componentStack && typeof obj.componentStack === 'string') {
            componentStack = obj.componentStack;
            continue;
          }
          try { messageParts.push(JSON.stringify(a).slice(0, 400)); } catch { messageParts.push(String(a)); }
        } else {
          messageParts.push(String(a));
        }
      }

      // 2. Полное сообщение — без агрессивной обрезки (slice 4000, хватит и на стек, и на mismatch detail).
      const message = messageParts.join(' ').slice(0, 4000);

      const shouldIgnoreConsole = CONSOLE_IGNORE_PATTERNS.some((re) => re.test(message));
      if (!shouldIgnoreConsole && message.length > 0) {
        // 3. Финальный stack = ошибка JS + componentStack (для декодирования имён компонентов).
        const combinedStack = [firstErrorStack, componentStack ? `\n\nComponent stack:${componentStack}` : null]
          .filter(Boolean)
          .join('');

        sendError({
          errorType: 'console',
          message,
          stack: combinedStack || undefined,
        });
      }
      addBreadcrumb({ type: 'console', message: message.slice(0, 200) });
    } catch {
      // не зацикливаемся
    }
    return origError.apply(console, args as Parameters<typeof console.error>);
  };
}

// ─── Breadcrumb collectors (что юзер делал перед ошибкой) ────────────────────

function trackUserInteractions(): void {
  if (typeof window === 'undefined') return;

  // Click
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const tag = target.tagName?.toLowerCase() || '';
    const text = (target.textContent || '').trim().slice(0, 60);
    const id = target.id ? `#${target.id}` : '';
    const cls = target.className && typeof target.className === 'string'
      ? `.${target.className.split(' ').slice(0, 2).join('.')}`
      : '';
    addBreadcrumb({
      type: 'click',
      message: `<${tag}${id}${cls}> "${text || '(пусто)'}"`.slice(0, 200),
    });
  }, true);

  // Navigation (через history API — pushState/popstate)
  const origPushState = history.pushState;
  history.pushState = function (...args: Parameters<typeof history.pushState>) {
    addBreadcrumb({ type: 'navigation', message: `→ ${args[2] || window.location.pathname}` });
    return origPushState.apply(this, args);
  };
  window.addEventListener('popstate', () => {
    addBreadcrumb({ type: 'navigation', message: `← ${window.location.pathname}` });
  });

  // Input changes (только blur, без значения для приватности)
  document.addEventListener('blur', (e) => {
    const target = e.target as HTMLInputElement | null;
    if (!target || (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA')) return;
    const name = target.name || target.id || target.type || 'input';
    const filled = target.value && target.value.length > 0 ? '✓' : '∅';
    addBreadcrumb({ type: 'input', message: `${name} ${filled}` });
  }, true);
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function initErrorTracker(): void {
  if (initialized) return;
  if (typeof window === 'undefined') return;

  // 1. Глобальные JS-ошибки + resource errors (capture phase для resource)
  window.addEventListener('error', onError, true);

  // 2. Unhandled Promise rejections
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  // 3. fetch monkey-patch
  patchFetch();

  // 4. XMLHttpRequest monkey-patch — ВКЛЮЧЁН.
  //    Зачем: критично для диагностики "сайт не работает у клиента".
  //    axios под капотом использует XHR — поэтому XHR patch ловит ВСЕ HTTP-запросы
  //    (axios, third-party libs, native XHR) единообразно. Это лучше чем дублирующий
  //    axios interceptor: axios interceptor отключён в lib/api.ts ради избежания дублей.
  patchXHR();

  // 5. console.error patch (фильтрует React warnings)
  patchConsoleError();

  // 6. Breadcrumbs — click / navigation / input для контекста
  trackUserInteractions();

  initialized = true;
}

/**
 * Программно добавить breadcrumb (например, из критичной бизнес-логики).
 */
export function addCustomBreadcrumb(message: string): void {
  addBreadcrumb({ type: 'custom', message: message.slice(0, 200) });
}

/**
 * Программная отправка ошибки — для использования в catch блоках.
 *   try { ... } catch (e) { logClientError('Custom error', e); }
 *
 * Авто-детект типа: если message начинается с "HTTP" или "Network error" — fetch,
 * иначе — console.
 */
export function logClientError(message: string, error?: Error | unknown): void {
  const err = error instanceof Error ? error : undefined;
  const isHttp = /^(HTTP \d|Network error)/i.test(message);
  // Извлекаем status code из сообщения вида "HTTP 404 GET /api/..."
  const statusMatch = message.match(/^HTTP (\d+)/);
  // Извлекаем method и url из "HTTP NNN METHOD /path..."
  const httpMatch = message.match(/^(?:HTTP \d+|Network error)\s+([A-Z]+)\s+(\S+)/);

  sendError({
    errorType: isHttp ? 'fetch' : 'console',
    message,
    stack: err?.stack,
    status: statusMatch ? parseInt(statusMatch[1], 10) : undefined,
    method: httpMatch ? httpMatch[1] : undefined,
    url: httpMatch ? httpMatch[2] : undefined,
  });
}
